/**
 * GlobalMusicBar — compact floating mini-player.
 *
 * Per ROS-203 spec: music note icon + skip back / play-pause / skip forward + close (X).
 * No album art, no progress bar, no track name. Floats bottom-right. The X button
 * both pauses playback AND dismisses the bar.
 */

import { useLocation } from 'react-router-dom';
import {
  SkipBack,
  SkipForward,
  Play,
  Pause,
  X,
  Music2
} from 'lucide-react';
import { useAudio } from '../context/AudioContext';

const GlobalMusicBar = () => {
  const location = useLocation();
  const {
    isPlaying,
    currentTrack,
    togglePlayPause,
    handlePreviousTrack,
    handleNextTrack,
    playerActive,
    setPlayerActive,
  } = useAudio();

  const path = location.pathname;

  // Hide on pages with their own music controls.
  if (path === '/music-player' || path === '/') return null;
  // Hide until the user has actually started playback (and not dismissed since).
  if (!currentTrack || !playerActive) return null;

  // Per ROS-203: X pauses playback AND dismisses the bar. The bar will
  // reappear once the user starts playback again from any surface.
  const handleClose = () => {
    if (isPlaying) togglePlayPause();
    setPlayerActive(false);
  };

  return (
    <div
      data-testid="global-music-bar"
      className="fixed bottom-[75px] right-10 z-30 flex items-center gap-1 bg-white dark:bg-lz-charcoal border border-lz-paper-border dark:border-lz-charcoal-border rounded-full shadow-lg px-2 py-1.5 transition-all duration-300"
    >
      {/* Music note indicator */}
      <div className="relative w-8 h-8 rounded-full bg-lz-concrete dark:bg-lz-ink-mid flex items-center justify-center flex-shrink-0">
        <Music2 className="w-4 h-4 text-lz-ember dark:text-lz-candle-dim" />
        {isPlaying && (
          <span className="absolute bottom-0 right-0 w-2 h-2 bg-lz-light-green rounded-full border border-white dark:border-lz-charcoal animate-pulse" />
        )}
      </div>

      {/* ⏮ Previous track */}
      <button
        onClick={handlePreviousTrack}
        className="p-1.5 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
        aria-label="Previous track"
      >
        <SkipBack className="w-4 h-4" />
      </button>

      {/* ⏸/▶ Play / Pause */}
      <button
        onClick={togglePlayPause}
        className="p-2 bg-lz-ember hover:bg-lz-ink-mid dark:bg-lz-candle-dim dark:hover:bg-lz-ink-mid text-white rounded-full transition-colors"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        <span className="flex items-center justify-center w-4 h-4">
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </span>
      </button>

      {/* ⏭ Next track */}
      <button
        onClick={handleNextTrack}
        className="p-1.5 text-black dark:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
        aria-label="Next track"
      >
        <SkipForward className="w-4 h-4" />
      </button>

      {/* Close — pauses music AND dismisses the bar */}
      <button
        onClick={handleClose}
        className="p-1.5 text-lz-ink-mid dark:text-lz-candle-dim hover:text-black dark:hover:text-lz-concrete hover:bg-lz-concrete dark:hover:bg-lz-ink-mid/60 rounded-full transition-colors"
        aria-label="Close player"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default GlobalMusicBar;
